import { Component, OnInit } from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { LocalStorage } from 'angular-web-storage';

import { ListResponse } from '../../../../../models/list-response';
import { getUserTypeDisplay, User, UserFilters, UserType } from '../../../../../models/user';

import { AccountService } from '../../../../../services/account/account.service';
import { UserService } from '../../../../../services/user/user.service';
import { CallbackResult, getStaffNameDisplay } from '../../../../../utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SimpleMessageModalComponent } from '../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { OrganizationConfig } from '../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { OrgPOSSoftware } from '../../../../../models/organization-config-enums';
import {
  SetUserLightspeedUserModalComponent
} from './user/modals/set-user-lightspeed-user-modal/set-user-lightspeed-user-modal.component';


/**
 * The component representing the list of gym staff user accounts.
 */
@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.css']
})
export class UserListComponent implements OnInit {

  public pageNumber = 1;
  @LocalStorage() public pageSize;
  public userFilters: UserFilters = {
    search: '',
    is_active: true,
  };

  public userFiltersChangedSubject = new Subject<null>();

  public userList: ListResponse<User>;
  public orgConfig: OrganizationConfig;

  public loadingUsers = true;
  public loadingSave = false;

  public successMessage = '';
  public errorMessage = '';

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  public readonly getStaffNameDisplay = getStaffNameDisplay;
  public readonly getUserTypeDisplay = getUserTypeDisplay;
  public readonly OrgPOSSoftware = OrgPOSSoftware;

  // The number of user list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private userListRequestCount = 0;

  constructor(
    public accountService: AccountService,
    private userService: UserService,
    private orgConfigService: OrganizationConfigService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().is_organization_admin) {
      return;
    }

    this.orgConfig = this.orgConfigService.getOrganizationConfig();
    if (this.orgConfig === null) {
      this.orgConfigService.loadOrganizationConfig().then(() => {
        this.orgConfig = this.orgConfigService.getOrganizationConfig();
      });
    }

    if (!this.pageSize) {
      this.pageSize = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSize = this.pageSize;

    // Make browser history available to the template.
    this.history = history;

    this.loadUsers();

    // Handle changes to the user filters by reloading the users, subject to
    // debounce.
    this.userFiltersChangedSubject.pipe(
      map(() => { this.loadingUsers = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadUsers();
    });
  }

  /**
   * Load the page of users corresponding to the current page and filter
   * selections.
   */
  public loadUsers() {
    this.loadingUsers = true;

    // Increment the request count and save a local copy.
    this.userListRequestCount++;
    const thisRequestCount = this.userListRequestCount;

    this.userService
      .getUsers(this.pageNumber, this.pageSize, this.userFilters)
      .then(response => {
        // Only update the user list of there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.userListRequestCount) {
          this.userList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.userListRequestCount) {
          this.loadingUsers = false;
        }
      });
  }

  /**
   * Update the current page number to the given value and load the page of
   * users.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadUsers();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.userList) { return 1; }
    return Math.ceil(this.userList.count / this.pageSize);
  }

  /**
   * Handle a change in the user filters. The list of users will be filtered
   * according to the new values.
   */
  public handleUserFilterChange() {
    this.pageNumber = 1;

    this.userFiltersChangedSubject.next();
  }

  public restoreUser(user: User) {
    this.successMessage = '';
    this.errorMessage = '';

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Restore Staff User';
    modalRef.componentInstance.messageText = (
      'Are you sure you want to restore ' +
      getStaffNameDisplay(user.first_name, user.last_name, user.email) +
      '?'
    );
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingSave = true;
      user.is_active = true;
      this.userService
        .setUserActiveStatus(user)
        .then(() => {
          this.loadUsers();
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errorMessage = 'Error removing user.';
            this.loadUsers();
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to restore this user.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSave = false; });
    }
  }

  public removeUser(user: User) {
    this.successMessage = '';
    this.errorMessage = '';

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Staff User';
    modalRef.componentInstance.messageText = (
      'Are you sure you want to remove ' +
      getStaffNameDisplay(user.first_name, user.last_name, user.email) +
      '? They will no longer be able to access the ' +
      (user.user_type === UserType.apiIntegration ? 'API.' : 'Staff Site.')
    );
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingSave = true;
      user.is_active = false;
      this.userService
        .setUserActiveStatus(user)
        .then(() => {
          this.loadUsers();
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errorMessage = 'Error removing user.';
            this.loadUsers();
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to remove this user.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSave = false; });
    }
  }

  public openSetLightspeedUserModal(user: User) {
    const modalRef = this.modalService.open(SetUserLightspeedUserModalComponent);
    modalRef.componentInstance.user = user;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.loadUsers();
    }
  }
}
